import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';
import { showErrorNotification, showSuccessNotification } from '../utils/notificationUtils';

// Cache for storing ETags
const etagCache = new Map<string, string>();

// Maps to backend/routers/financial.py
export function useItems(accessToken: string) {
    const queryClient = useQueryClient();

    // The main query to fetch items
    const {
        data: items,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['items'],
        queryFn: async () => {
            if (!accessToken) {
                return [];
            }

            try {
                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${accessToken}`,
                };

                // Add If-None-Match header if we have a cached ETag
                const cachedETag = etagCache.get('items');
                if (cachedETag) {
                    headers['If-None-Match'] = cachedETag;
                }

                const response = await fetch(`${env.API_URL}/api/financial/items`, {
                    headers,
                    credentials: 'include',
                });

                // If we get a 304 Not Modified, use the cached data
                if (response.status === 304) {
                    console.log('Using cached items data (304 Not Modified)');
                    return queryClient.getQueryData<any[]>(['items']) || [];
                }

                // Store the new ETag for future requests
                const newETag = response.headers.get('ETag');
                if (newETag) {
                    etagCache.set('items', newETag);
                }

                // Check if the response is valid
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to fetch items');
                }

                const data = await response.json();
                return data;
            } catch (err) {
                console.error('Error fetching items:', err);
                throw err;
            }
        },
        staleTime: 300000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    // Mutation for deleting an item
    const deleteItemMutation = useMutation({
        mutationFn: async (itemId: string) => {
            const response = await fetch(`${env.API_URL}/api/financial/items/${itemId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Failed to disconnect account');
            }

            return itemId;
        },
        onSuccess: (_, itemId) => {
            showSuccessNotification('Successfully disconnected account');

            // Invalidate accounts and items queries to refresh the data
            queryClient.invalidateQueries({ queryKey: ['accounts'] });
            queryClient.invalidateQueries({ queryKey: ['items'] });
        },
        onError: (error) => {
            showErrorNotification(error, 'Failed to disconnect account. Please try again.');
        }
    });

    // Mutation for getting a refresh link token
    const getRefreshTokenMutation = useMutation({
        mutationFn: async ({
            itemId,
            accountSelectionEnabled = false
        }: {
            itemId: string;
            accountSelectionEnabled?: boolean
        }) => {
            // Get the current URL for use as redirect_uri for OAuth flow
            const redirect_uri = `${window.location.origin}/plaid-oauth-redirect`;

            const response = await fetch(`${env.API_URL}/api/financial/items/${itemId}/refresh`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    redirect_uri,
                    force_reauthorization: true,
                    account_selection_enabled: accountSelectionEnabled,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to initiate reauthorization');
            }

            const data = await response.json();
            return {
                linkToken: data.link_token,
                expires: data.expires,
                itemId
            };
        }
    });

    return {
        items: items || [],
        isLoading,
        error,
        refetch,
        deleteItem: deleteItemMutation.mutate,
        isDeleting: deleteItemMutation.isPending,
        getRefreshToken: getRefreshTokenMutation.mutateAsync,
        isGettingRefreshToken: getRefreshTokenMutation.isPending,
    };
} 